import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye.svg';
import { ReactComponent as EyeSlashIcon } from '../../assets/icons/eye-slash.svg';
import { postLogin } from '../../utils/backend';
import AnsaLogo from '../../assets/images/ansa/ansa-logo.svg';
import { cilLockLocked, cilUser } from '@coreui/icons';
import { toast } from 'react-toastify';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const Login = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const initialValues = {
    email: '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be between 6 and 100 characters')
      .max(100, 'Password must be between 6 and 100 characters'),
  });

  const handleLogin = async (values, { setSubmitting, setErrors, setFieldTouched }) => {
    setLoading(true);
    try {
      const response = await postLogin(values);

      if (!response || response.status === 502 || response.status === 503) {
        throw new Error('Server is down. Please try again later');
      }

      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed', error);
      toast.error(error.message);
      setErrors({ submit: 'Login failed', ...error.fieldErrors });
      Object.keys(values).forEach(field => setFieldTouched(field, true, false));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        <img src={AnsaLogo} alt="Ansa Logo" />
      </div>
      <div className="login-right">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md={8}>
              <CCard className="p-4 border-light">
                <CCardBody>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleLogin}
                  >
                    {({ isSubmitting, errors, touched }) => (
                      <Form>
                        <h2>Admin Portal Sign In</h2>
                        <p className="text-body-secondary">Welcome to ANSA Admin Portal</p>
                        {loading ? (
                          <div className="text-center">
                            <CSpinner color="primary" />
                          </div>
                        ) : (
                          <>
                            {errors.submit && (
                              <div className="alert alert-danger">{errors.submit}</div>
                            )}
                            <CInputGroup className="mb-3">
                              <CInputGroupText>
                                <CIcon icon={cilUser} />
                              </CInputGroupText>
                              <Field
                                name="email"
                                as={CFormInput}
                                placeholder="Email"
                                autoComplete="email"
                                className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                              />
                              <ErrorMessage name="email" component="div" className="invalid-feedback" />
                            </CInputGroup>
                            <CInputGroup className="mb-4">
                              <CInputGroupText>
                                <CIcon icon={cilLockLocked} />
                              </CInputGroupText>
                              <Field
                                name="password"
                                as={CFormInput}
                                type={isPasswordVisible ? 'text' : 'password'}
                                placeholder="Password"
                                autoComplete="current-password"
                                className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                              />
                              <CInputGroupText onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                                {isPasswordVisible ? <EyeIcon style={{ width: '20px', height: '20px' }} /> : <EyeSlashIcon style={{ width: '20px', height: '20px' }} />}
                              </CInputGroupText>
                              <ErrorMessage name="password" component="div" className="invalid-feedback" />
                            </CInputGroup>
                            <CRow>
                              <CCol xs={6}>
                                <CButton type="submit" className="ansa-button-primary px-4" disabled={isSubmitting}>
                                  Login
                                </CButton>
                              </CCol>
                              <CCol xs={6} className="text-end">
                                <Link to="/forgot-password">
                                  <CButton color="link" className="px-0">
                                    Forgot password?
                                  </CButton>
                                </Link>
                              </CCol>
                            </CRow>
                          </>
                        )}
                      </Form>
                    )}
                  </Formik>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    </div>
  );
};

export default Login;